import React, { useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Camera,
  ChevronLeft,
  FolderSymlink,
  Grid,
  Justify,
  Pencil,
  Search,
  Trash,
  X,
} from "react-bootstrap-icons";
import { IMoveItems } from "../../types";
import { moveItem, renameFolder } from "../../graphql/state";
import { useReactiveVar } from "@apollo/client";
import useGetUrlLocale from "../../hooks/useGetUrlLocale";
import {
  usePagesByIdsByAdminLazyQuery,
  useUpdatePageScreeShotMutation,
} from "../../graphql/page.hooks";
import { makePagesScreenShots } from "../../utils/page/makePagesScreenShots";
import { useUserQuery } from "../../graphql/menu.hooks";
import { Spinner } from "react-bootstrap";

type SearchBarProps = {
  t: any;
  title: string;
  searchTerm?: string;
  setSearchTerm?: React.Dispatch<React.SetStateAction<string>>;
  viewMode?: "grid" | "table";
  setViewMode?: React.Dispatch<React.SetStateAction<"grid" | "table">>;
  handleSearch?: () => void;
  link?: string;
  className?: string;
  selectedItems?: IMoveItems;
  setSelectedItems?: React.Dispatch<React.SetStateAction<IMoveItems>>;
  setShowDeleteModal?: React.Dispatch<React.SetStateAction<boolean>>;
  isProjects?: boolean;
  isDisabled: boolean;
  isAdminTable?: boolean;
};

const SearchBar: React.FC<SearchBarProps> = ({
  t,
  title,
  searchTerm,
  setSearchTerm,
  viewMode,
  setViewMode,
  handleSearch,
  link,
  className,
  selectedItems,
  setSelectedItems,
  setShowDeleteModal,
  isProjects = false,
  isDisabled,
  isAdminTable = false,
}) => {
  const { id, projectId } = useParams();
  const navigate = useNavigate();
  const lng = useGetUrlLocale();

  const [makeScreen] = useUpdatePageScreeShotMutation();
  const [getPages] = usePagesByIdsByAdminLazyQuery();
  const { data: userData } = useUserQuery();

  const moveItemState = useReactiveVar(moveItem);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const searchPlaceholder = isAdminTable
    ? "Search by Title, Url, Id"
    : window.innerWidth <= 767 && !isProjects
      ? t("projects.search").slice(0, 6) + "..."
      : t("projects.search");

  const focusToSearchInput = () => {
    if (searchRef?.current) {
      searchRef.current.focus();
    }
  };

  const editItemHandler = () => {
    if (!id) {
      navigate(`${lng}/projects/${projectId}/edit`);
      return;
    }

    renameFolder({ isActive: true, folderId: +id });
  };

  const tryMakeScreenShots = async () => {
    setIsLoading(true);
    const { data } = await getPages({
      variables: { ids: selectedItems?.pageIds },
    });

    await makePagesScreenShots(data?.pagesByIdsByAdmin, makeScreen);
    setSelectedItems({ pageIds: [], folderIds: [] });
    setIsLoading(false);
  };

  return (
    <div className="bg-white rounded px-3 px-md-4 mb-4">
      <div
        className={`d-flex flex-column flex-md-row align-items-md-center justify-content-${isAdminTable ? "end" : "between"} table-heading`}
      >
        {!isAdminTable && (
          <div className="d-flex align-items-center pb-lg-0 pb-3 col-12 col-md-3 col-lg-4 col-xxl-5">
            {!isProjects && (
              <Link
                to={link || "#"}
                className={`me-3 text-reset ${className || ""}`}
              >
                <ChevronLeft className="fs-5" />
              </Link>
            )}
            <div className="d-flex align-items-center col-9 col-md-10 col-lg-12 p-0">
              <h5
                className="d-none d-md-block text-nowrap fs-4 text-truncate text-dark-gray mb-0 lh-sm"
                style={{ maxWidth: "100%" }}
              >
                {title}
              </h5>
              <h4
                className="d-block d-md-none text-nowrap fs-5 text-truncate text-dark-gray mb-0 lh-sm"
                style={{ maxWidth: "100%" }}
              >
                {title}
              </h4>
              <Pencil
                onClick={editItemHandler}
                className={`cursor-pointer fs-6 text-dark-gray ${isProjects ? "d-none" : "d-block"} ms-2`}
              />
            </div>
          </div>
        )}
        <div className="d-flex align-items-center justify-content-between">
          <div
            className={`${isProjects ? "col-8 d-flex align-items-center" : "col-4"} position-relative col-md-auto`}
          >
            <input
              ref={searchRef}
              type="text"
              placeholder={searchPlaceholder}
              className="form-control border-0 border-bottom rounded-0 pe-4 pe-md-5 ps-0 ps-md-auto pb-2 pb-md-auto"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) =>
                e.key === "Enter" && handleSearch && handleSearch()
              }
            />
            {!searchTerm ? (
              <>
                <Search
                  onClick={focusToSearchInput}
                  className={`${isDisabled ? "text-gray" : "text-dark"} ${isProjects && "d-none d-md-block"} position-absolute translate-middle-y top-50 end-0 me-2`}
                />
                {isProjects && (
                  <Search
                    onClick={focusToSearchInput}
                    className={`${isDisabled ? "text-gray" : "text-dark"} d-block d-md-none fs-4 mt-1 ms-3`}
                  />
                )}
              </>
            ) : (
              <X
                onClick={() => setSearchTerm("")}
                size={24}
                className="cursor-pointer position-absolute translate-middle-y top-50 end-0 me-2"
              />
            )}
          </div>
          <div
            className={`border-line mx-3 mx-sm-4  ${isProjects && "d-none"}`}
          ></div>
          <div className={`d-flex ${isProjects && "d-none"}`}>
            <FolderSymlink
              onClick={() =>
                moveItem({
                  ...moveItemState,
                  data: selectedItems,
                  isActive: true,
                })
              }
              className={`searchbar-icon cursor-pointer d-block ${selectedItems?.folderIds.length || selectedItems?.pageIds.length ? "text-primary" : "text-light disabled-block"} fs-20 me-3 mb-xxl-0`}
            />
            <Trash
              onClick={() => setShowDeleteModal(true)}
              className={`searchbar-icon cursor-pointer d-block ${selectedItems?.folderIds.length || selectedItems?.pageIds.length ? "text-primary" : "text-light disabled-block"} fs-20`}
            />
            {userData?.user?.admin &&
              (!isLoading ? (
                <Camera
                  onClick={() => tryMakeScreenShots()}
                  className={`searchbar-icon cursor-pointer d-block ms-3 ${
                    selectedItems?.pageIds.length &&
                    !selectedItems?.folderIds.length
                      ? "text-primary"
                      : "text-light disabled-block"
                  } fs-20`}
                />
              ) : (
                <Spinner className="ms-3" animation="border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </Spinner>
              ))}
          </div>
          <div className={`border-line mx-3 mx-sm-4 ${className || ""}`}></div>
          <div className={`d-flex ${className || ""}`}>
            {!isAdminTable && (
              <button
                disabled={isDisabled}
                className={`btn btn-outline-primary primary-hover border-0 bg-white ${isProjects && "p-2"} ${viewMode === "grid" && !isDisabled ? "text-primary" : isDisabled ? "text-light" : "text-dark"}`}
                onClick={() => setViewMode && setViewMode("grid")}
              >
                <Grid className="fs-5" />
              </button>
            )}
            <button
              disabled={isDisabled}
              className={`btn btn-outline-primary primary-hover border-0 bg-white ${isProjects && "p-2"} ${viewMode === "table" && !isDisabled ? "text-primary" : isDisabled ? "text-light" : "text-dark"}`}
              onClick={() => setViewMode && setViewMode("table")}
            >
              <Justify className="fs-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SearchBar;
